import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import type { SchemaElement } from "../../api/restaurantSchema";
import type { OpenTable, Reservation } from "../../api/reservations";
import { useSchemaStage } from "../../components/restaurant-schema/useSchemaStage";
import { useOnReservationsSourceChange } from "../../hooks/useEdgeMode";
import CloudLinkBanner from "../../components/CloudLinkBanner";
import type { ModuleOutletContext } from "../../components/moduleSidebar/ModuleSidebarLayout";
import { useRestaurantSchema } from "../../hooks/useRestaurantSchema";
import { useViewport } from "../../hooks/useViewport";
import { useAppSelector } from "../../store/hooks";
import { createFloorSnapshot } from "./logic/tableStatus";
import { listTabOf, type ListTab } from "./logic/reservationList";
import { canRestore, seatCheckOf, upcomingActionsOf } from "./logic/reservationActions";
import { deriveReservations } from "./logic/settleClosedBills";
import { canLinkToSchema, canOpenSchemaPage, showsSchemaSetup } from "./logic/schemaSetup";
import { newReservationStartFor, slotAtMoment } from "./logic/newReservation";
import { momentAtMinute } from "./logic/dayTime";
import { momentFocusOf, timelineLoadOf, timelineMarkersOf } from "./logic/momentFocus";
import { useNow } from "./hooks/useNow";
import { useReservationsView, type NewReservationSeed } from "./hooks/useReservationsView";
import { useReservationActions } from "./hooks/useReservationActions";
import { useNewReservationForm } from "./hooks/useNewReservationForm";
import { useTablePicker } from "./hooks/useTablePicker";
import { useSuggestTable } from "./hooks/useSuggestTable";
import { isTillUnavailable, useReservationsEdge } from "./hooks/useReservationsEdge";
import ReservationsTopBar from "./components/ReservationsTopBar";
import DayNavigator from "./components/DayNavigator";
import ReservationRegionTabs from "./components/ReservationRegionTabs";
import FloorToolbar from "./components/FloorToolbar";
import ReservationsSideControls from "./components/ReservationsSideControls";
import Timeline from "./components/Timeline";
import FloorCanvas from "./components/FloorCanvas";
import FloorLegend from "./components/FloorLegend";
import PageNotice, { SchemaSetupNotice } from "./components/PageNotice";
import ReservationsList from "./components/list/ReservationsList";
import NewReservationPanel from "./components/newReservation/NewReservationPanel";
import MobileTablePicker from "./components/newReservation/MobileTablePicker";
import RegionGrid from "./components/RegionGrid";
import type { PopoverHandlers } from "./components/popover/TablePopoverBody";

const NO_OPEN_TABLES: OpenTable[] = [];
const NO_RESERVATIONS: Reservation[] = [];

/**
 * Модул „Резервации": горна лента с деня, табове на регионите, времева
 * линия, схемата със статусите на масите в избрания момент (поповър при
 * клик) и дясна колона — списъкът с резервации или „Нова резервация".
 * Цялото състояние е в `useReservationsView`; данните се опресняват на 15 s.
 */
const Reservations = () => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { onOpenMobileMenu } = useOutletContext<ModuleOutletContext>();
  // Под 1024 px (lg) дясната колона е под платното.
  const stacked = !isDesktop;
  // Срещу каса без резервации страницата е заключена; каса, която ги умее,
  // ги сервира и без интернет (`reservationsLocked` е false).
  const { isEdgeLocal, locked: reservationsLocked, dataAsOf } = useReservationsEdge();
  const nowMs = useNow();

  // Без интернет схемата е огледалото на касата — не я poll-ваме.
  const schemaQuery = useRestaurantSchema({ live: !isEdgeLocal });
  const regions = useMemo(() => schemaQuery.data ?? [], [schemaQuery.data]);
  const canOpenSchema = useAppSelector((state) => canOpenSchemaPage(state.auth));

  const view = useReservationsView({ regions, nowMs, enabled: !reservationsLocked });
  const { dayQuery } = view;
  const openTables = dayQuery.data?.openTables ?? NO_OPEN_TABLES;

  // Настанената със закрита сметка е вече приключила (масата е свободна, без
  // да чака BE изчакването на картовото плащане), тази с отворена сметка е
  // на масата на сметката си (сметката може да е преместена в Маркиране), а
  // предстоящата с изтекъл край е „Неизпълнена" — огледало на BE
  // синхронизацията, за да не чака следващия GET.
  const schemaTables = useMemo(
    () =>
      regions.flatMap((candidate) =>
        candidate.elements
          .filter((element) => element.kind === "table")
          .map((table) => ({ id: table.id, name: table.name ?? "" })),
      ),
    [regions],
  );
  const reservations = useMemo(
    () => deriveReservations(view.reservations, openTables, schemaTables, nowMs),
    [view.reservations, openTables, schemaTables, nowMs],
  );

  // Настанените СЕГА извън деня (изгледът „Утре"/„Вчера") — само за
  // проверката на „Настани": масата им е заета, но не са в списъка/линията.
  const seatedNowRaw = dayQuery.data?.seatedNow ?? NO_RESERVATIONS;
  const seatedNow = useMemo(
    () => deriveReservations(seatedNowRaw, openTables, schemaTables, nowMs),
    [seatedNowRaw, openTables, schemaTables, nowMs],
  );

  const [listTab, setListTab] = useState<ListTab>("upcoming");
  // Телефон: регионът филтрира списъка (схемата там не се показва); null — всички.
  const [phoneRegionFilter, setPhoneRegionFilter] = useState<number | null>(null);

  // Действията на предстояща: „Настани" само днес (или вчерашна, чийто час
  // още тече след полунощ — вижда се и в изгледа за вчера), с маса и без маса
  // на друга настанена резервация (и в деня, и сега настанена извън него) и
  // без отворена сметка на предишна резервация; сметките без резервация по
  // масите са кандидатите за закачане (BE ги връща сегашните за всеки ден).
  const actionsFor = (
    reservation: Reservation,
    dayReservations: Reservation[],
    daySeatedNow: Reservation[],
    dayOpenTables: OpenTable[],
    atMs: number,
  ) =>
    upcomingActionsOf(
      reservation,
      atMs,
      seatCheckOf(reservation, [...dayReservations, ...daySeatedNow], dayOpenTables),
    );
  const actionsOfUpcoming = (reservation: Reservation) =>
    actionsFor(reservation, reservations, seatedNow, openTables, nowMs);

  // „Настани" решава по ПРЕСНИТЕ данни: сметка, отворена в Маркиране след
  // последния poll, иначе не се вижда и резервацията се закача към
  // единствената позната, без да пита при кой сервитьор. Неуспешно
  // опресняване оставя последните данни (react-query ги пази).
  const loadSeat = async (picked: Reservation) => {
    const result = await dayQuery.refetch();
    const day = result.data;
    const dayOpenTables = day?.openTables ?? openTables;
    const freshNowMs = Date.now();
    const dayReservations = day
      ? deriveReservations(day.reservations, dayOpenTables, schemaTables, freshNowMs)
      : reservations;
    const daySeatedNow = day
      ? deriveReservations(day.seatedNow ?? NO_RESERVATIONS, dayOpenTables, schemaTables, freshNowMs)
      : seatedNow;
    const fresh = dayReservations.find((candidate) => candidate.id === picked.id);
    return fresh ? actionsFor(fresh, dayReservations, daySeatedNow, dayOpenTables, freshNowMs) : null;
  };

  // След „Настани" линията се връща към „сега" — иначе, закована на минал
  // час (закъсняла резервация), масата изглежда „Свободна". След „Върни" /
  // „Отмени настаняването" резервацията е пак в „Предстоящи" — подчертана.
  // Кандидатите на модала „При кой сервитьор" — по ПРЕСНАТА резервация
  // (настанена междувременно от друг терминал → няма кандидати, модалът се
  // затваря).
  const actions = useReservationActions({
    nowMs,
    loadSeat,
    seatBillsOf: (picked) => {
      const fresh = reservations.find((candidate) => candidate.id === picked.id);
      return fresh ? actionsOfUpcoming(fresh)?.seatBills ?? [] : [];
    },
    onSeated: view.goToNow,
    onBackToUpcoming: (reservation) => {
      setListTab("upcoming");
      view.highlightReservation(reservation);
    },
    // Без интернет „Отделна сметка за всяка маса" е изключена (касата отваря една).
    isEdgeLocal,
  });

  const snapshot = useMemo(
    () =>
      createFloorSnapshot({
        reservations,
        openTables,
        momentMs: view.momentMs,
        nowMs,
        isAtNow: view.isAtNow,
      }),
    [reservations, openTables, view.momentMs, nowMs, view.isAtNow],
  );

  // Линията: точките на резервациите и графиката „натовареност" на деня.
  const timelineMarkers = useMemo(
    () => timelineMarkersOf(reservations, view.dateKey),
    [reservations, view.dateKey],
  );
  const timelineLoad = useMemo(
    () => timelineLoadOf(reservations, view.dateKey, view.timelineRange, nowMs),
    [reservations, view.dateKey, view.timelineRange, nowMs],
  );
  // „Преглед към": кои резервации са на масите в избрания момент (дясната
  // колона ги подчертава). Само когато часът е закрепен с линията — на „сега"
  // и в друг ден без пипната линия списъкът е както винаги.
  const momentFocus = useMemo(
    () => (!view.isPinned || view.isDayPending ? null : momentFocusOf(reservations, view.momentMs, nowMs, view.dateKey)),
    [view.isPinned, view.isDayPending, reservations, view.momentMs, nowMs, view.dateKey],
  );

  const region = view.region;
  const stage = useSchemaStage({ region });
  // Таблет: платното взима височината на схемата, не фиксираните 70% от екрана.
  const tabletFit = isTablet && !isMobile && stage.fitHeight !== null;

  // „Нова резервация": формата (дясната колона) + режимът избор на платното.
  const isNewOpen = view.rightPanel === "new";
  const form = useNewReservationForm({ regions, nowMs, open: isNewOpen, enabled: !reservationsLocked });
  const picker = useTablePicker({ form, open: isNewOpen });
  // На телефон схемата не се показва при „Нова резервация" — регион и маса се избират във формата.
  const pickOnPhone = isMobile && isNewOpen;

  const openNewReservation = (seed: NewReservationSeed | null = null) => {
    form.reset(seed);
    view.openNewReservation();
  };

  // „Редактирай" — същият панел, попълнен с резервацията (PATCH).
  const openEditReservation = (reservation: Reservation) => {
    form.startEdit(reservation);
    view.highlightReservation(reservation);
    view.openNewReservation();
  };

  // Смяна облак ↔ каса: избраното (поповър, резервация, редакция) е от
  // данните на другата страна — затваря се, вместо да сочи несъществуващо.
  // Само срещу каса с резервации; иначе страницата просто се заключва.
  useOnReservationsSourceChange(() => {
    view.closePopover();
    view.selectReservation(null);
    if (form.editing) {
      view.closeNewReservation();
      form.reset(null);
    }
  });

  const suggestTable = useSuggestTable(form);
  const handleSuggestTable = () =>
    suggestTable(view.region?.id ?? null, (table) => view.focusTable(table.id, { openPopover: false }));

  const handleSaved = (saved: Reservation) => {
    view.closeNewReservation();
    setListTab("upcoming");
    view.revealReservation(saved);
  };

  const selectedReservation = reservations.find((item) => item.id === view.selectedReservationId);
  const highlightedTableIds = useMemo(
    () => new Set(selectedReservation?.tableIds ?? []),
    [selectedReservation],
  );

  // Esc затваря поповъра (preventDefault — иначе минава и глобалното „назад").
  // Докато е отворен модал (сервитьорът, потвърждение), Esc затваря само него.
  const { openTableId, closePopover } = view;
  const isModalOpen = actions.isPickingBill || actions.isConfirming;
  useEffect(() => {
    if (openTableId === null || isModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      closePopover();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openTableId, closePopover, isModalOpen]);

  const activeReservations = reservations.filter((item) => item.status !== "cancelled");

  // Телефон: резервациите на региона (по масите му); без маса (изтрита) — само във „Всички".
  const isInRegion = (reservation: Reservation, regionId: number) => {
    const target = regions.find((item) => item.id === regionId);
    return Boolean(target?.elements.some((element) => reservation.tableIds.includes(element.id)));
  };
  const listReservations =
    isMobile && phoneRegionFilter !== null
      ? reservations.filter((reservation) => isInRegion(reservation, phoneRegionFilter))
      : reservations;
  const selectPhoneRegion = (regionId: number | null) => {
    setPhoneRegionFilter(regionId);
    // Новата резервация започва от този регион.
    if (regionId !== null) view.selectRegion(regionId);
  };
  const handleTableClick = (table: SchemaElement) => {
    if (view.isDayPending) return;
    const state = snapshot.stateOf({ id: table.id, name: table.name ?? "" });
    const reservation = state.kind === "free" ? null : state.reservation;
    view.selectReservation(reservation?.id ?? null);
    // Резервацията може да е в друг таб — иначе картата ѝ не е в DOM.
    if (reservation) setListTab(listTabOf(reservation));
    view.openPopover(table.id);
  };

  // Под платното (stacked) списъкът е под сгъвката: клик на карта връща
  // платното във видимата част, иначе поповърът се отваря извън екрана.
  const canvasRef = useRef<HTMLDivElement>(null);
  const handleCardSelect = (reservation: Reservation) => {
    view.focusReservation(reservation);
    // На телефон схемата не се показва — без невидим поповър (иначе той
    // изяжда първото Esc) и без превъртане към нея.
    if (isMobile) view.closePopover();
    else if (stacked) canvasRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // „Резервирай тази маса" — само ако в/след момента има слот в същия ден.
  const reserveSlot = slotAtMoment(view.dateKey, view.momentMs, nowMs);

  const popoverHandlers: PopoverHandlers = {
    onSeat: (reservation) => void actions.seat(reservation),
    onRelease: (reservation) => void actions.release(reservation),
    onCancel: (reservation) => void actions.cancel(reservation),
    onEdit: openEditReservation,
    onUnseat: (reservation) => void actions.unseat(reservation),
    onRestore: (reservation) => void actions.restore(reservation),
    restorableOn: (tableId) =>
      selectedReservation && canRestore(selectedReservation) && selectedReservation.tableIds.includes(tableId)
        ? selectedReservation
        : null,
    actionsOf: actionsOfUpcoming,
    onReserveTable: reserveSlot
      ? () =>
          openNewReservation({
            tableId: view.openTableId,
            startMs: momentAtMinute(reserveSlot.dateKey, reserveSlot.minute),
          })
      : null,
    pendingAction: actions.pendingAction,
  };

  const dayNavigator = (
    <DayNavigator
      dateKey={view.dateKey}
      todayKey={view.todayKey}
      compact={isNarrow}
      fill={isNarrow}
      onChange={view.goToDay}
    />
  );

  const handleNewReservation = () => {
    // Разглеждан друг ден → формата е за него, не за днес.
    const startMs = newReservationStartFor(view);
    openNewReservation(startMs === null ? null : { tableId: null, startMs });
  };

  // Без регион (зарежда се, грешка, няма схема) — само денят над съобщението,
  // за да може да се смени (напр. при грешка за един ден).
  const withDayOnly = (notice: ReactNode) => (
    <>
      <FloorToolbar regionTabs={null} dayNavigator={dayNavigator} onNewReservation={null} isNarrow={isNarrow} />
      {notice}
    </>
  );

  const renderBody = () => {
    if (reservationsLocked) {
      return <PageNotice tone="offline" title={t("edge.unavailableOffline")} hint={t("reservations.offlineHint")} />;
    }
    // Касата отказва (503): изгледът ѝ е стар или денят е извън прозореца ѝ.
    // Денят остава сменяем — близките до днес дни може да ги има. Без
    // условие „няма данни": при отказ TanStack пази кеша отпреди прекъсването
    // (облачните данни), а касата отказва всяко действие върху тях.
    if (isEdgeLocal && (isTillUnavailable(schemaQuery.error) || isTillUnavailable(dayQuery.error))) {
      return withDayOnly(
        <PageNotice tone="offline" title={t("edge.unavailableOffline")} hint={t("reservations.offlineStaleHint")} />,
      );
    }
    if (schemaQuery.isLoading || (dayQuery.isLoading && !dayQuery.data)) {
      return withDayOnly(<PageNotice tone="loading" />);
    }
    // Само без данни: неуспешен фонов poll (isError при налични данни) не бива
    // да сменя цялата страница с грешка — остават последните данни.
    if ((schemaQuery.isError && !schemaQuery.data) || (dayQuery.isError && !dayQuery.data)) {
      return withDayOnly(
        <PageNotice tone="error" title={t("reservations.loadError")} hint={t("reservations.loadErrorHint")} />,
      );
    }
    // Без нито една маса и с право за схемата — пътят към нея вместо празното
    // платно (без правото екранът е както досега).
    if (showsSchemaSetup({ regions: schemaQuery.data, canOpenSchema, isEdgeLocal })) {
      return <SchemaSetupNotice />;
    }
    if (!region) {
      return withDayOnly(
        <PageNotice
          tone="empty"
          title={t("reservations.noSchema.title")}
          hint={t("reservations.noSchema.description")}
        />,
      );
    }

    return (
      <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:flex-row">
        {/* Телефон + „Нова резервация": схемата е скрита (не размонтирана) — масите се избират във формата. */}
        <section className={`${pickOnPhone ? "hidden" : "flex"} min-w-0 flex-col gap-3 lg:min-h-0 lg:flex-1`}>
          <FloorToolbar
            regionTabs={
              isMobile ? (
                <RegionGrid
                  regions={regions}
                  activeId={phoneRegionFilter}
                  onChange={selectPhoneRegion}
                  captionOf={(item) =>
                    t("reservations.counts.reservations", {
                      count: activeReservations.filter((reservation) => isInRegion(reservation, item.id)).length,
                    })
                  }
                  all={{
                    label: t("reservations.regionAll"),
                    caption: t("reservations.counts.reservations", { count: activeReservations.length }),
                  }}
                />
              ) : (
                <ReservationRegionTabs
                  regions={regions}
                  activeId={region.id}
                  onChange={view.selectRegion}
                />
              )
            }
            // Широк екран: денят и „Нова резервация" са над списъка.
            dayNavigator={stacked ? dayNavigator : null}
            onNewReservation={stacked ? handleNewReservation : null}
            isNarrow={isNarrow}
          />
          <div
            ref={canvasRef}
            // Телефон: без схемата (там не е удобна) — регионите филтрират списъка.
            className={`${isMobile ? "hidden" : "flex"} ${tabletFit ? "" : "h-[70dvh] min-h-[420px]"} scroll-mt-3 flex-col overflow-hidden rounded-[14px] border border-shade-2 bg-surface lg:h-auto lg:min-h-0 lg:flex-1`}
          >
            {!picker && (
              <Timeline
                momentMs={view.momentMs}
                dateKey={view.dateKey}
                nowMs={nowMs}
                sliderMinute={view.sliderMinute}
                range={view.timelineRange}
                isOutside={view.isOutsideTimeline}
                isAtNow={view.isAtNow}
                isNarrow={isNarrow}
                markers={timelineMarkers}
                load={timelineLoad}
                onChange={view.setMomentMinute}
                onNow={view.goToNow}
              />
            )}
            {/* Таблет: схемата е висока точно колкото вписания регион (без празно отгоре/отдолу). */}
            <div
              className={tabletFit ? "flex max-h-[70dvh] min-h-[280px] flex-col" : "contents"}
              style={tabletFit ? { height: stage.fitHeight ?? undefined } : undefined}
            >
              <FloorCanvas
                stage={stage}
                region={region}
                snapshot={snapshot}
                highlightedTableIds={highlightedTableIds}
                openTableId={view.openTableId}
                isPending={view.isDayPending}
                isNarrow={isNarrow}
                popoverHandlers={popoverHandlers}
                picker={picker}
                canLinkToSchema={canLinkToSchema({ canOpenSchema, isEdgeLocal })}
                onTableClick={handleTableClick}
                onClosePopover={view.closePopover}
              />
            </div>
            <FloorLegend mode={picker ? "picker" : "status"} stage={stage} isNarrow={isNarrow} />
          </div>
        </section>

        <aside className="flex flex-col gap-3 lg:min-h-0 lg:w-[380px] lg:shrink-0">
          {!stacked && (
            <ReservationsSideControls
              dayNavigator={
                <DayNavigator dateKey={view.dateKey} todayKey={view.todayKey} compact={false} fill onChange={view.goToDay} />
              }
              onNewReservation={isNewOpen ? null : handleNewReservation}
            />
          )}
          {isNewOpen ? (
            <NewReservationPanel
              form={form}
              todayKey={view.todayKey}
              nowMs={nowMs}
              isNarrow={isNarrow}
              stacked={stacked}
              onSuggest={handleSuggestTable}
              onClose={view.closeNewReservation}
              onSaved={handleSaved}
              tablePicker={
                pickOnPhone ? (
                  <MobileTablePicker
                    form={form}
                    regions={regions}
                    activeRegionId={region.id}
                    onRegionChange={view.selectRegion}
                    onSuggest={handleSuggestTable}
                  />
                ) : null
              }
            />
          ) : (
            <ReservationsList
              reservations={listReservations}
              regions={regions}
              activeRegionId={isMobile ? (phoneRegionFilter ?? region.id) : region.id}
              tab={listTab}
              selectedReservationId={view.selectedReservationId}
              nowMs={nowMs}
              isNarrow={isNarrow}
              stacked={stacked}
              schemaHidden={isMobile}
              actions={actions}
              upcomingActionsOf={actionsOfUpcoming}
              isLoading={view.isDayPending}
              onTabChange={setListTab}
              onSelect={handleCardSelect}
              onEdit={openEditReservation}
              momentFocus={momentFocus}
            />
          )}
        </aside>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-canvas">
      <ReservationsTopBar isNarrow={isNarrow} onOpenMobileMenu={onOpenMobileMenu} />
      {/* Срещу касата лентата носи и „· данни към 14:32" — кога касата е видяла облака. */}
      {isEdgeLocal && <CloudLinkBanner detail={dataAsOf} />}
      <div
        className={`flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:overflow-hidden ${
          isNarrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-6 pt-4"
        }`}
      >
        {renderBody()}
      </div>
      {actions.billPicker}
      {actions.confirmModal}
    </div>
  );
};

export default Reservations;
